import { useQuery, useMutation } from "@tanstack/react-query";
import { fetch3, fetch4, fetch5 } from "../commonApis/commonApis";
import { AddressOption } from "../types/types";
const url = process.env.REACT_APP_BASE_URL;


interface CountryPayload {
  countryName?: string;
  countryCode: string;
  currencyCode: string;
  dialCode: number;
  id?: string;
  type?: string;
}

interface HubPayload {
  id?: string;
  type?: string;
  name?: string;
  hubName?: string;
  countryId: string;
}

interface CityPayload {
  id?: string;
  name: string;
  cityCode: string;
  arabicName: string;
  countryId: string;
  hubId: string;
  type?: string;
}

interface ApiResponse {
  success: boolean;
  data: any;
  message?: string;
  statusCode?: number;
  statusMessage?: string;
  error?: any;
}

export const useIntegrationData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["countriesData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries?page=${page}&searchFor=${searchFor}&type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchCountriesAllData = () => {
  return useQuery({
    queryKey: ["countriesAllData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/all?type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchCountryWarehouseAllData = () => {
  return useQuery({
    queryKey: ["warehouseAllData"], // include country in key
    queryFn: async () => {
      const res = await fetch3(`${url}/api/warehouse?type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    // enabled: !!country, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchCountryHubsAllData = (country: string) => {
  return useQuery({
    queryKey: ["countryHubsAllData", country], // include country in key
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/${country}/hubs?type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!country, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchHubsCitiesAllData = (hubs: string) => {
  return useQuery({
    queryKey: ["hubsCitiesAllData", hubs], // include hubs in key
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/${hubs}/citiesData?type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!hubs, // ❗️Don't run until a country is selected
    staleTime: 1000 * 60 * 5,
  });
};


export const useFetchCountryCitiesAllData = (
  countryId: string | undefined, 
  searchRef?: string,
  selectedOption?: AddressOption
) => {
  const shouldIgnoreSearchRef = selectedOption === 'short' || selectedOption === 'map' || selectedOption === undefined;
  const queryKeySearchRef = shouldIgnoreSearchRef ? null : searchRef;

  return useQuery({
    queryKey: ["countryCitiesAllData", countryId, queryKeySearchRef],
    queryFn: async () => {
      if (!countryId) {
        return []; // Return empty array if no countryId
      }
      
      const searchParam = shouldIgnoreSearchRef ? '' : (searchRef || '');
      const res = await fetch3(
        `${url}/api/countries/${countryId}/cities?type=LM&searchFor=${searchParam}`, 
        "get"
      );
      
      if (!res?.data) {
        throw new Error(res?.message || "Failed to fetch cities");
      }
      
      return res.data;
    },
    enabled: !!countryId && (shouldIgnoreSearchRef || !!searchRef),
    staleTime: 1000 * 60 * 5,
    retry: false
  });
};

export const useFetchCountryCitiesArrayData = (citiesArray: any) => {
  return useQuery({
    queryKey: ["countryCitiesArrayData", citiesArray], // include country in key
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/cities-data?name=${JSON.stringify(citiesArray)}&type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!citiesArray?.length, // ❗️Run when names are provided
    staleTime: 1000 * 60 * 5,
  });
};

export const useFetchCountryCitiesDistrictsArrayData = (districtsArray: any) => {
  return useQuery({
    queryKey: ["countryCityDistrictsArrayData", districtsArray], // include country in key
    queryFn: async () => {
      const res = await fetch3(`${url}/api/countries/districts-data?name=${JSON.stringify(districtsArray)}&type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message;
      }

      return res.data;
    },
    enabled: !!districtsArray?.length, // ❗️Run when names are provided
    staleTime: 1000 * 60 * 5,
  });
};


export const useHubsFetchData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["hubsFetchData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/hubs?page=${page}&searchFor=${searchFor}&type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCitiesFetchData = (page: number, searchFor: string) => {
  return useQuery({
    queryKey: ["citiesFetchData"],
    queryFn: async () => {
      const res = await fetch3(`${url}/api/cities?page=${page}&searchFor=${searchFor}&type=lmClient`, "get");

      if (!res || !res.data) {
        return res.message
      }

      return res.data; // 👈 This returns your full user object
    },
    staleTime: 1000 * 60 * 5,
  });
};


export const useDistrictFetchData = (cityId?: string, searchRef?: string, selectedOption?: AddressOption) => {
  const isShortOrMapSelected = selectedOption === 'short' || selectedOption === 'map' || selectedOption === undefined;
  const queryKeySearchRef = isShortOrMapSelected ? null : searchRef;

  return useQuery({
    queryKey: ["districtFetchData", cityId, queryKeySearchRef],
    queryFn: async () => {
      if (!cityId) return []; // Return empty array if no city selected
      
      const searchParam = isShortOrMapSelected ? '' : (searchRef || '');
      const res = await fetch3(`${url}/api/cities/${cityId}/districts?searchFor=${searchParam}`, "get");
      return res?.data || [];
    },
    enabled: !!cityId && (isShortOrMapSelected || !!searchRef),
    staleTime: 1000 * 60 * 5,
  });
};

export const useCountriesCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: CountryPayload) => {
      const res: ApiResponse = await fetch4(`${url}/api/countries?type=lmClient`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ Country Created successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Country create request failed:', error.message);
    }
  });
};

export const useHubCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: HubPayload) => {
      const res: ApiResponse = await fetch4(`${url}/api/hubs?type=lmClient`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ Hub Created successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Hub create request failed:', error.message);
    }
  });
};

export const useCityCreateMutation = () => {
  return useMutation({
    mutationFn: async (payload: CityPayload) => {
      const res: ApiResponse = await fetch4(`${url}/api/cities?type=lmClient`, payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return res.data;
    },
    onSuccess: (data: any) => {
      console.log('✅ City Created successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ City create request failed:', error.message);
    }
  });
};


export const useCountryUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: CountryPayload) => {
      const res: ApiResponse = await fetch5(`${url}/api/countries/${payload?.id}?type=lmClient`, "PATCH", payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return { ...res.data, message: res.message };
    },
    onSuccess: (data) => {
      console.log('✅ Country updated successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Country update request failed:', error.message);
    }
  });
};

export const useHubUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: HubPayload) => {
      const res: ApiResponse = await fetch5(`${url}/api/hubs/${payload?.id}?type=lmClient`, "PATCH", payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return { ...res.data, message: res.message };
    },
    onSuccess: (data) => {
      console.log('✅ Hub updated successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ Hub update request failed:', error.message);
    }
  });
};

export const useCityUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: CityPayload) => {
      const res: ApiResponse = await fetch5(`${url}/api/cities/${payload?.id}?type=lmClient`, "PATCH", payload);

      // Manually throw error if success is false
      if (!res.success) {
        throw res.message || 'Something went wrong';
      }

      return { ...res.data, message: res.message };
    },
    onSuccess: (data) => {
      console.log('✅ City updated successfully:', data);
    },
    onError: (error: any) => {
      console.error('❌ City update request failed:', error.message);
    }
  });
};

export const useFetchCountryCitiesUpdateAllData = (countryId: string | undefined) => {
  return useQuery({
    queryKey: ["countryCitiesAllData", countryId],
    queryFn: async () => {
      if (!countryId) {
        return []; // Return empty array if no countryId
      }

      const res = await fetch3(`${url}/api/countries/${countryId}/cities?type=LM&searchFor=${''}`, "get");

      if (!res?.data) {
        throw new Error(res?.message || "Failed to fetch cities");
      }

      return res.data;
    },
    enabled: !!countryId, // Only enable when countryId exists
    staleTime: 1000 * 60 * 5,
    retry: false
  });
};

export const useDistrictFetchUpdateData = (cityId?: string) => {
  return useQuery({
    queryKey: ["districtFetchData", cityId],
    queryFn: async () => {
      if (!cityId) return []; // Return empty array if no city selected
      
      const res = await fetch3(`${url}/api/cities/${cityId}/districts?searchFor=${''}`, "get");
      return res?.data || [];
    },
    enabled: !!cityId, // Only enable when cityId exists
    staleTime: 1000 * 60 * 5,
  });
};